import {RootState} from 'app/rootReducer';
import {SliceStatus} from 'interfaces';

import {createSelector} from '@reduxjs/toolkit';

import {MessagingState} from './messagingSlice';

/**
 * Selector function to get the `MessagingState` from the root state.
 * @param state - The root state of the Redux store.
 * @returns The `MessagingState` slice of the state.
 */
function getMessaging(state: RootState): MessagingState {
  return state.messaging;
}

export const selectMessagesForConversation = createSelector(
  [
    getMessaging,
    (_: RootState, conversationId: string): string => conversationId,
  ],
  (state, conversationId) => {
    /**
     *
     */
    const messageIds = state?.conversations?.[conversationId]?.messages ?? [];
    return messageIds
      .map(id => state?.messages?.[id] ?? null)
      .filter(message => message !== null);
  },
);

export const selectConversationMetadata = createSelector(
  [
    getMessaging,
    (_: RootState, conversationId: string): string => conversationId,
  ],
  (state, conversationId) => {
    /**
     *
     */
    const lastMessageId =
      state?.conversations?.[conversationId]?.lastMessageId ?? null;
    /**
     *
     */
    const hasMore = state?.conversations?.[conversationId]?.hasMore ?? false;
    return {
      lastMessageId,
      hasMore,
    };
  },
);

/**
 * Selector function to get external connection data
 */
export const selectExternalConnection = createSelector(
  getMessaging,
  state => state.externalConnection,
);

/**
 * Selector function to get message metadata
 */
export const selectMessageMetadata = createSelector(
  getMessaging,
  state => state.messageMetadata,
);
/**
 * Selector function to get `refresh` state.
 */
export const selectRefreshingState = createSelector(
  getMessaging,
  list => list.refreshingState,
);

/**
 * select connections state
 */
export const selectConnectionsState = createSelector(
  getMessaging,
  messaging => ({
    isResolved: messaging.connections?.status === SliceStatus.resolved,
    isRejected: messaging.connections?.status === SliceStatus.rejected,
    isPending: messaging.connections?.status === SliceStatus.pending,
    isIdle: messaging.connections?.status === SliceStatus.idle,
  }),
);

/**
 * getConnections
 */
export const selectConnections = createSelector(
  getMessaging,
  messaging => messaging.connections?.data,
);

/**
 * get search connection keyword
 */
export const selectSearchKeyword = createSelector(
  getMessaging,
  messaging => messaging?.searchConnection?.keyword,
);
/**
 * get search connection state
 */
export const selectSearchConnectionsState = createSelector(
  getMessaging,
  messaging => ({
    isResolved: messaging?.searchConnection?.status === SliceStatus.resolved,
    isRejected: messaging?.searchConnection?.status === SliceStatus.rejected,
    isPending: messaging?.searchConnection?.status === SliceStatus.pending,
    isIdle: messaging?.searchConnection?.status === SliceStatus.idle,
  }),
);

/**
 * get connections copy
 */
export const selectConnectionsCopy = createSelector(
  getMessaging,
  messaging => messaging.connectionsCopy,
);

export const selectParticipantId = createSelector(
  getMessaging,
  messaging => messaging.participantId,
);

export const selectUnreadAllMessageCount = createSelector(
  getMessaging,
  messaging => messaging.unreadAllMessageCount,
);
